import React, { CSSProperties, useCallback, useState } from 'react'
import {
    IRadioSubscriptionPackage,
    ISingleSubscriptionPackage,
    SubscriptionPriceObject,
} from '../../helpers/BreachWallTypes'
import { PackageOptions, BreachOptions } from './SubscriptionPackage.styled'
import {
    StyledSubscriptionOriginalPrice,
    StyledSubscriptionPrice,
    StyledSubscriptionPriceQualifier,
    StyledSubscriptionRadioContainer,
    StyledSubscriptionRadioPrice,
    StyledSubscriptionRadioInput,
    StyledSubscriptionRadioInputLabel,
    StyledSubscriptionRadioQualifier,
    StyledSubscriptionRadioRibbon,
    StyledSubscriptionRadioRow,
    StyledSubscriptionBillingCycle,
} from './SubscriptionPriceDisplay.styled'

interface BasePriceDisplayProps {
    packageOptions: PackageOptions
    breachOptions: BreachOptions
}

export interface SingleSubscriptionPriceProps extends BasePriceDisplayProps {
    subscriptionPackage: ISingleSubscriptionPackage
}

/**
 * Display the original subscription price, the current price, and the price qualifier, this will not "radio-ify" the results at all.
 */
export const SingleSubscriptionPriceDisplay = ({
    subscriptionPackage,
    packageOptions,
    breachOptions,
}: SingleSubscriptionPriceProps) => {
    const { price, originalPrice, billingCycle, priceQualifier } = subscriptionPackage

    return (
        <React.Fragment>
            {originalPrice && (
                <StyledSubscriptionOriginalPrice
                    packageOptions={packageOptions}
                    breachOptions={breachOptions}
                >
                    {originalPrice}
                </StyledSubscriptionOriginalPrice>
            )}

            <StyledSubscriptionPrice
                packageOptions={packageOptions}
                breachOptions={breachOptions}
            >
                {price}
            </StyledSubscriptionPrice>

            {billingCycle && (
                <StyledSubscriptionBillingCycle
                    packageOptions={packageOptions}
                    breachOptions={breachOptions}
                >
                    {billingCycle}
                </StyledSubscriptionBillingCycle>
            )}

            {priceQualifier && (
                <StyledSubscriptionPriceQualifier
                    packageOptions={packageOptions}
                    breachOptions={breachOptions}
                >
                    {priceQualifier}
                </StyledSubscriptionPriceQualifier>
            )}
        </React.Fragment>
    )
}

export interface RadioSubscriptionPriceDisplayProps
    extends BasePriceDisplayProps {
    subscriptionPackage: IRadioSubscriptionPackage
    selectedPricePoint: SubscriptionPriceObject
    setSelectedPricePoint?: (newPricePoint: SubscriptionPriceObject) => void
    packagePrices: SubscriptionPriceObject[]
}

export const RadioSubscriptionPriceDisplay = ({
    subscriptionPackage,
    selectedPricePoint,
    setSelectedPricePoint,
    packagePrices,
}: RadioSubscriptionPriceDisplayProps) => {
    const getPricePointComponents = useCallback(
        () =>
            packagePrices.map((pricePoint, index) => {
                const {
                    price,
                    priceQualifier,
                    ribbonOptions,
                    ribbonTitle,
                    packagePath,
                } = pricePoint

                // Common details for both single and multi price points
                const subDetails = (
                    <>
                        <StyledSubscriptionRadioPrice>
                            {price}
                        </StyledSubscriptionRadioPrice>
                        {priceQualifier && (
                            <StyledSubscriptionRadioQualifier>
                                {priceQualifier}
                            </StyledSubscriptionRadioQualifier>
                        )}
                        {ribbonOptions && ribbonTitle && (
                            <StyledSubscriptionRadioRibbon
                                ribbonOptions={ribbonOptions}
                            >
                                {ribbonTitle}
                            </StyledSubscriptionRadioRibbon>
                        )}
                    </>
                )

                // If there's more than one price point, we need to show the radio buttons
                return subscriptionPackage.prices.length > 1 ? (
                    <StyledSubscriptionRadioRow key={packagePath}>
                        <StyledSubscriptionRadioInput
                            type="radio"
                            name={subscriptionPackage.heading}
                            id={packagePath}
                            value={packagePath}
                            checked={
                                selectedPricePoint.packagePath === packagePath
                            }
                            onChange={() => setSelectedPricePoint?.(pricePoint)}
                        />
                        <StyledSubscriptionRadioInputLabel
                            htmlFor={packagePath}
                        >
                            {subDetails}
                        </StyledSubscriptionRadioInputLabel>
                    </StyledSubscriptionRadioRow>
                ) : (
                    <StyledSubscriptionRadioRow
                        key={packagePath}
                        onClick={() => setSelectedPricePoint?.(pricePoint)}
                    >
                        {subDetails}
                    </StyledSubscriptionRadioRow>
                )
            }),
        [
            packagePrices,
            selectedPricePoint,
            setSelectedPricePoint,
            subscriptionPackage,
        ],
    )

    return (
        <StyledSubscriptionRadioContainer role="radiogroup">
            {getPricePointComponents()}
        </StyledSubscriptionRadioContainer>
    )
}
